import { useState, useEffect, useCallback } from "react";

// @mui material components
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

// Images
import loginImage from "assets/images/login.png";
import fitifyLogo from "assets/images/nFitifyLogo.svg";
import { getAuth } from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";
import { userSelector, clearState, loginUser } from "store/features/authSlice";
import { toast } from "react-toastify";
import { validateEmail } from "utils/common";
import { useNavigate } from "react-router-dom";
import { Icon } from "@mui/material";
import RenewSubscriptionModal from "../renewPlan/Renew";
import { resetRedirectToPayment } from "store/features/authSlice";

function SignIn() {
  const dispatch = useDispatch();
  const {
    isSuccess,
    isError,
    isFetching,
    message,
    redirectToPayment,
    referrals,
    uid,
    auth: isAuth,
  } = useSelector(userSelector);

  const userDetails = useSelector(userSelector);

  const navigate = useNavigate();

  useEffect(() => {
    if (isError && !isSuccess) {
      toast.error(message);
    }

    if (isSuccess && !isError) {
      toast.success(message);
    }

    if (isSuccess && !redirectToPayment && isAuth) {
      navigate("/dashboard");
    }
    clearState();
  }, [isError, message, dispatch, isSuccess, navigate, isAuth]);

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch, isError, message, dispatch, isSuccess, isAuth]);

  const auth = getAuth();
  const [formdata, setFormdata] = useState({
    email: "",
    password: "",
  });

  const errordefault = {
    email: false,
    password: false,
  };

  const [errorMessage, setErrorMessage] = useState(errordefault);

  const loginHandler = useCallback(() => {
    if (!validateEmail(formdata.email)) {
      toast.error("Email is Invalid");
      setErrorMessage({
        ...errorMessage,
        email: true,
      });
      return;
    }

    dispatch(
      loginUser({ auth, email: formdata.email, password: formdata.password })
    );
  }, [auth, dispatch, errorMessage, formdata.email, formdata.password]);

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        // event.preventDefault();
        loginHandler();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [loginHandler]);

  const [showPass, setShowPass] = useState(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#1A1A1A",
        display: "flex",
        alignItems: "center",
        fontFamily: "Outfit",
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4} alignItems="stretch">
          {/* Left Side - Image */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: { xs: "400px", md: "600px" },
                borderRadius: "20px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={loginImage}
                alt="Login"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: "20px",
                padding: { xs: 3, sm: 4, md: 5 },
                textAlign: "center",
                height: { xs: "600px", md: "600px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={fitifyLogo}
                  alt="Fitify Logo"
                  onClick={() => navigate("/")}
                  sx={{
                    height: "60px",
                    width: "auto",
                    maxWidth: "200px",
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </Box>

              {/* Title */}
              <Typography
                variant="h4"
                sx={{
                  color: "#17C1E8",
                  fontWeight: "bold",
                  mb: 1,
                  fontFamily: "Outfit",
                }}
              >
                Welcome Back!
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  color: "#FFFFFF",
                  mb: 4,
                  fontFamily: "Outfit",
                  opacity: 0.8,
                }}
              >
                Enter your email and password to sign in to your account
              </Typography>

              {/* Login Form */}
              <Box component="form" sx={{ textAlign: "left" }}>
                {/* Email Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Email*
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="Enter your email"
                    value={formdata.email}
                    error={errorMessage?.email}
                    onChange={(e) => {
                      setErrorMessage({
                        ...errorMessage,
                        email: false,
                      });
                      setFormdata({
                        ...formdata,
                        email: e.target.value,
                      });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        borderRadius: "10px",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "2px solid #17C1E8",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontFamily: "Outfit",
                        padding: "12px 16px",
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Password*
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      fullWidth
                      type={showPass ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formdata.password}
                      error={errorMessage?.password}
                      onChange={(e) => {
                        setErrorMessage({
                          ...errorMessage,
                          password: false,
                        });
                        setFormdata({
                          ...formdata,
                          password: e.target.value,
                        });
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFFFFF",
                          borderRadius: "10px",
                          paddingRight: "50px",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "2px solid #17C1E8",
                          },
                        },
                        "& .MuiInputBase-input": {
                          fontFamily: "Outfit",
                          padding: "12px 16px",
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => setShowPass(!showPass)}
                      sx={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#666",
                      }}
                    >
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Forgot Password */}
                <Box sx={{ mb: 3, textAlign: "right" }}>
                  <Typography
                    variant="body2"
                    onClick={() => navigate("/forgot-password")}
                    sx={{
                      color: "#17C1E8",
                      cursor: "pointer",
                      fontFamily: "Outfit",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Forgot password?
                  </Typography>
                </Box>

                {/* Submit Button */}
                <Button
                  fullWidth
                  variant="contained"
                  disabled={isFetching}
                  onClick={loginHandler}
                  sx={{
                    backgroundColor: "#17C1E8",
                    color: "#000000",
                    fontWeight: "bold",
                    padding: "12px",
                    borderRadius: "10px",
                    fontSize: "16px",
                    fontFamily: "Outfit",
                    textTransform: "none",
                    mb: 3,
                    "&:hover": {
                      backgroundColor: "#14A8C4",
                    },
                    "&:disabled": {
                      backgroundColor: "#666",
                      color: "#999",
                    },
                  }}
                >
                  {isFetching ? "Signing In..." : "Sign In"}
                </Button>

                {/* Sign Up Link */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Outfit",
                    }}
                  >
                    Don't have an account?{" "}
                    <Typography
                      component="span"
                      onClick={() => navigate("/register")}
                      sx={{
                        color: "#17C1E8",
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontFamily: "Outfit",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Sign up
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Renew Subscription Modal */}
      <RenewSubscriptionModal
        open={Boolean(redirectToPayment)}
        onClose={() => {
          dispatch(resetRedirectToPayment());
          dispatch(clearState());
        }}
        userData={userDetails}
        referrals={referrals}
        uid={uid}
        onRenewSuccess={() => {}}
      />
    </Box>
  );
}

export default SignIn;
